import { useEffect, useMemo, useState } from 'react';
import { usePosthog } from './usePosthog';

export type TFeatureFlagVariantStatus = 'loading' | 'enabled' | 'disabled';

/** Tri-state flag check. Distinguishes "not resolved" from "non-matching variant". */
export const useFeatureFlagVariantState = (
  flagKey: string,
  expectedVariant: string,
): TFeatureFlagVariantStatus => {
  const posthogService = usePosthog();

  // Resolve immediately if flags are already loaded, otherwise start as 'loading'
  const initialStatus = useMemo((): TFeatureFlagVariantStatus => {
    if (!posthogService) return 'disabled';
    if (!posthogService.areFlagsReady()) return 'loading';
    return posthogService.isFeatureFlagVariant(flagKey, expectedVariant) ? 'enabled' : 'disabled';
  }, [posthogService, flagKey, expectedVariant]);

  const [status, setStatus] = useState<TFeatureFlagVariantStatus>(initialStatus);

  useEffect(() => {
    if (!posthogService) {
      setStatus('disabled');
      return;
    }

    const sync = () => {
      const matched = posthogService.isFeatureFlagVariant(flagKey, expectedVariant);
      setStatus(matched ? 'enabled' : 'disabled');
    };

    posthogService.onFeatureFlags(sync);
  }, [flagKey, expectedVariant, posthogService]);

  return status;
};
